import React, { useState, useEffect } from "react";
import api from "../../../../api";
import {
 Card,
 Row,
 Col,
 Badge,
 Table,
 Modal,
 Button,
 ProgressBar,
} from "react-bootstrap";
import {
 PieChart,
 Pie,
 Cell,
 Tooltip,
 Legend,
} from "recharts";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";

const COLORS = ["#28A745", "#FFC107", "#6C757D"];

const JMRTab = () => {
 const { id: workOrderId } = useParams();

 const [jmrs, setJmrs] = useState([]);
 const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0 });
 const [loading, setLoading] = useState(true);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [billedIds, setBilledIds] = useState(new Set());

 const [selectedJMR, setSelectedJMR] = useState(null);
 const [showModal, setShowModal] = useState(false);

 // =====================================
 // FETCH PROJECT-WISE JMRs
 // =====================================
 useEffect(() => {
 if (workOrderId) fetchJMR();
 }, [workOrderId]);

 const fetchJMR = async () => {
 try {
 setLoading(true);
 const [res, billingRes] = await Promise.all([
 api.get("/jmr", { params: { workOrder: workOrderId } }),
 api.get("/billing", { params: { workOrder: workOrderId } }),
 ]);

 if (res.data.success) {
 const data = res.data.data;
 setJmrs(data);

 const approved = data.filter((x) => x.approvalStatus === "Approved").length;
 const pending = data.filter((x) => x.approvalStatus !== "Approved").length;

 setStats({ total: data.length, approved, pending });
 }

 if (billingRes.data.success) {
 const ids = new Set();
 (billingRes.data.data || []).forEach((b) => {
 (b.linkedReports || []).forEach((group) => {
 (group.reportIds || []).forEach((id) => ids.add(String(id)));
 });
 });
 setBilledIds(ids);
 }
 } catch (err) {
 Swal.fire("Error", "Failed to load JMRs", "error");
 } finally {
 setLoading(false);
 }
 };

 const approvalPercent =
 stats.total === 0 ? 0 : (stats.approved / stats.total) * 100;

 const pieData = [
 { name: "Approved", value: stats.approved },
 { name: "Pending", value: stats.pending },
 ];

 const formatDate = (d) =>
 d
 ? new Date(d).toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 })
 : "—";

 const getBadge = (st) => {
 if (st === "Approved") return "success";
 if (st === "Pending") return "warning";
 return "secondary";
 };

 // =====================================
 // PDF ACTIONS
 // =====================================
 const handlePreviewPDF = async (id) => {
 try {
 setPreviewingPdf(true);
 const res = await api.get(`/jmr/${id}/pdf`, { responseType: "blob" });
 const url = URL.createObjectURL(new Blob([res.data]));
 window.open(url, "_blank");
 } catch (err) {
 Swal.fire("Error", "Failed to preview PDF", "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleDownloadPDF = async (id) => {
 try {
 const res = await api.get(`/jmr/${id}/pdf`, { responseType: "blob" });
 const a = document.createElement("a");
 a.href = URL.createObjectURL(new Blob([res.data]));
 a.download = `JMR-${id}.pdf`;
 a.click();
 } catch (err) {
 Swal.fire("Error", "Failed to download PDF", "error");
 }
 };

 const handleView = async (id) => {
 try {
 const res = await api.get(`/jmr/${id}`);
 if (res.data.success) {
 setSelectedJMR(res.data.data);
 setShowModal(true);
 }
 } catch (err) {
 Swal.fire("Error", "Failed to load details", "error");
 }
 };

 if (loading) return <p>Loading JMR data...</p>;

 return (
 <>
 <Row className="g-4">

 {/* SUMMARY CARD */}
 <Col md={4}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="fw-bold mb-3">JMR Summary</h5>
 <p>Total JMRs: <strong>{stats.total}</strong></p>
 <p className="text-success">Approved: <strong>{stats.approved}</strong></p>
 <p className="text-warning">Pending: <strong>{stats.pending}</strong></p>
 <ProgressBar
 now={approvalPercent}
 label={`${approvalPercent.toFixed(0)}% Approved`}
 />
 </Card.Body>
 </Card>
 </Col>

 {/* PIE CHART */}
 <Col md={4}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="fw-bold mb-3">JMR Status Distribution</h5>
 <PieChart width={260} height={260}>
 <Pie
 data={pieData}
 dataKey="value"
 nameKey="name"
 cx="50%"
 cy="50%"
 outerRadius={90}
 label
 >
 {pieData.map((d, i) => (
 <Cell key={i} fill={COLORS[i]} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </Card.Body>
 </Card>
 </Col>

 {/* RECENT JMR TABLE */}
 <Col md={12}>
 <Card className="shadow-sm border-0">
 <Card.Body>
 <h5 className="fw-bold mb-3">JMR List</h5>
 <Table hover responsive>
 <thead>
 <tr>
 <th>#</th>
 <th>JMR No</th>
 <th>Status</th>
 <th>Billing</th>
 <th>Installed Date</th>
 <th>Submitted</th>
 <th>PDF</th>
 <th>View</th>
 </tr>
 </thead>
 <tbody>
 {jmrs.map((j, i) => (
 <tr key={j._id}>
 <td>{i + 1}</td>
 <td><strong>{j.jmrNumber}</strong></td>
 <td><Badge className="rounded-pill" bg={getBadge(j.approvalStatus)}>{j.approvalStatus}</Badge></td>
 <td>
 {billedIds.has(String(j._id)) ? (
 <Badge className="rounded-pill" bg="success">Billed</Badge>
 ) : (
 <Badge className="rounded-pill" bg="secondary">Unbilled</Badge>
 )}
 </td>
 <td>{formatDate(j.installationDate)}</td>
 <td>{j.submittedBy?.name || "N/A"}</td>
 <td>
 <VerifiedCopyActions 
 id={j._id} 
 type="jmr" 
 hasVerifiedCopy={!!j.verifiedCopy?.filePath} 
 onUploadSuccess={fetchJMR}
 />
 </td>
 <td>
 <button
 className="btn btn-sm btn-outline-primary"
 onClick={() => handleView(j._id)}
 >
 <i className="fas fa-eye"></i>
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* VIEW MODAL */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>JMR Details</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedJMR && (
 <>
 <Row>
 <Col md={6}><strong>JMR Number:</strong> {selectedJMR.jmrNumber}</Col>
 <Col md={6}><strong>Status:</strong> <Badge className="rounded-pill ms-2" bg={getBadge(selectedJMR.approvalStatus)}>{selectedJMR.approvalStatus}</Badge></Col>
 <Col md={6}><strong>Date:</strong> {formatDate(selectedJMR.installationDate)}</Col>
 <Col md={6}><strong>Submitted:</strong> {selectedJMR.submittedBy?.name || "N/A"}</Col>
 </Row>

 {selectedJMR.materials?.length > 0 && (
 <>
 <hr />
 <h6 className="fw-bold">Installed Materials</h6>
 <ul>
 {selectedJMR.materials.map((m, idx) => (
 <li key={idx}>
 {m.itemName} — {m.installedQty} {m.unit}
 </li>
 ))}
 </ul>
 </>
 )}
 </>
 )}
 </Modal.Body>
 <Modal.Footer>
 {selectedJMR && (
 <Button variant="primary" onClick={() => handlePreviewPDF(selectedJMR._id)}>
 Preview PDF
 </Button>
 )}
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>

 {/* LOADER OVERLAY */}
 {previewingPdf && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
 style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
 >
 <div className="bg-white p-3 rounded shadow">
 Preparing JMR PDF…
 </div>
 </div>
 )}
 </>
 );
};

export default JMRTab;
